/**
 * Tactical Theme Patterns Module
 * 
 * Contains predefined patterns for common tactical themes in chess.
 * Used by both the SequenceGenerator and TacticalThemeClassifier modules.
 */

import { TacticalHint } from '../../types/chess';

/**
 * Tactical pattern definition
 */
export interface TacticalPattern {
  fen: string;             // Starting position
  moves: string[];         // Sequence of moves
  description: string;     // Description of the tactical motif
}

/**
 * Enhanced tactical pattern with hint information
 */
export interface EnhancedTacticalPattern extends TacticalPattern {
  /** Tactical hints for this pattern */
  hints: TacticalHint[];
  /** Difficulty rating (1-5 scale) */
  difficulty: number;
  /** Common mistakes players make with this pattern */
  commonMistakes?: string[];
}

/**
 * Collection of predefined tactical patterns organized by theme
 */
export const TACTICAL_PATTERNS: Record<string, TacticalPattern[]> = {
  // Fork patterns (one piece attacks multiple targets)
  fork: [
    {
      fen: 'r1bqkbnr/pppp1ppp/2n5/4p3/4P3/5N2/PPPP1PPP/RNBQKB1R w KQkq - 2 3',
      moves: ['Ng5', 'Be6', 'Nxe6'],
      description: 'Knight fork attacking queen and rook'
    },
    {
      fen: 'rnbqkb1r/pp1p1ppp/5n2/2p1p3/2B1P3/5N2/PPPP1PPP/RNBQK2R w KQkq - 0 4',
      moves: ['Ng5', 'd6', 'Nf7'],
      description: 'Knight fork targeting king and rook'
    },
    {
      fen: 'r1bqkbnr/pppp1ppp/8/4p3/3n4/2N5/PPPPPPPP/R1BQKBNR w KQkq - 0 3',
      moves: ['Ne4', 'Qh4', 'Nf6+'],
      description: 'Knight fork attacking king and queen'
    }
  ],
  
  // Pin patterns (piece immobilized due to piece of higher value behind it)
  pin: [
    {
      fen: 'rnbqkbnr/ppp2ppp/8/3pp3/4P3/5N2/PPPP1PPP/RNBQKB1R w KQkq - 0 3',
      moves: ['Bb5', 'c6', 'Bd7+'],
      description: 'Bishop pin against king'
    },
    {
      fen: 'rnbqk2r/pppp1ppp/5n2/2b1p3/2B1P3/5N2/PPPP1PPP/RNBQK2R w KQkq - 4 4',
      moves: ['Bd5', 'c6', 'Bxf7+'],
      description: 'Bishop pin exploiting the f7 weakness'
    },
    {
      fen: 'r1bqkb1r/pppp1ppp/2n2n2/4p3/3P4/2N1PN2/PPP2PPP/R1BQKB1R b KQkq - 0 4',
      moves: ['Bb4', 'Bd2', 'Bxc3'],
      description: 'Bishop pin against knight'
    }
  ],
  
  // Discovered Attack patterns (moving one piece reveals attack from another)
  discoveredAttack: [
    {
      fen: 'r1bqkbnr/ppp2ppp/2n5/3pp3/3PP3/2N5/PPP2PPP/R1BQKBNR w KQkq - 0 4',
      moves: ['Nf3', 'dxe4', 'Ng5'],
      description: 'Knight discovered attack on queen'
    },
    {
      fen: 'rnbqkbnr/ppp2ppp/8/3p4/3Pp3/2N5/PPP2PPP/R1BQKBNR w KQkq - 0 5',
      moves: ['Bg5', 'f6', 'Bh4'],
      description: 'Bishop discovered attack on pawn'
    },
    {
      fen: 'r1bqk2r/pppp1ppp/2n2n2/2b1p3/2B1P3/2NP1N2/PPP2PPP/R1BQK2R b KQkq - 0 5',
      moves: ['Bxf2+', 'Kxf2', 'Ne4+'],
      description: 'Knight discovered check after bishop sacrifice'
    }
  ],
  
  // Sacrifice patterns (intentional material loss for positional gain)
  sacrifice: [
    {
      fen: 'r1bqkb1r/pppp1ppp/2n2n2/4p3/2B1P3/5N2/PPPP1PPP/RNBQK2R w KQkq - 4 4',
      moves: ['Nxe5', 'Nxe5', 'd4'],
      description: 'Knight sacrifice to gain central control'
    },
    {
      fen: 'rnbqkbnr/pppp1ppp/8/4p3/4P3/5N2/PPPP1PPP/RNBQKB1R b KQkq - 1 2',
      moves: ['Nf6', 'Ne5', 'd6'],
      description: 'Knight sacrifice to open attack line'
    },
    {
      fen: 'rnbqkb1r/pppp1ppp/5n2/4p3/2B1P3/5N2/PPPP1PPP/RNBQK2R w KQkq - 2 3',
      moves: ['Bxf7+', 'Kxf7', 'Ng5+'],
      description: 'Bishop sacrifice to expose king'
    }
  ],
  
  // Checkmate patterns (forcing moves leading to checkmate)
  checkmate: [
    {
      fen: 'r1bqkbnr/pppp1ppp/2n5/4p3/2B1P3/5N2/PPPP1PPP/RNBQK2R w KQkq - 2 3',
      moves: ['Bxf7+', 'Kxf7', 'Ne5+', 'Ke8', 'Qh5+', 'g6', 'Qxe8#'],
      description: 'Scholar\'s mate variation'
    },
    {
      fen: 'r1bqkb1r/pppp1Qpp/2n2n2/4p3/2B1P3/8/PPPP1PPP/RNB1K1NR b KQkq - 0 4',
      moves: ['Qe7', 'Bxf7+', 'Kd8', 'Qf8#'],
      description: 'Classic queen and bishop mate'
    }
  ]
};

/**
 * Gets all available tactical themes
 * @returns Array of available themes
 */
export function getAvailableTacticalThemes(): string[] {
  return Object.keys(TACTICAL_PATTERNS);
}

/**
 * Gets a description for a tactical theme
 * @param theme The tactical theme
 * @returns Description of the theme
 */
export function getTacticalThemeDescription(theme: string): string {
  switch(theme) {
    case 'fork':
      return 'A fork is a tactic where a single piece attacks two or more opponent pieces simultaneously.';
    case 'pin':
      return 'A pin is a tactic where a piece is prevented from moving because moving would expose a more valuable piece behind it.';
    case 'discoveredAttack':
      return 'A discovered attack occurs when a piece moves and reveals an attack from another piece that was behind it.';
    case 'sacrifice':
      return 'A sacrifice involves deliberately giving up material to gain a positional advantage or initiative.';
    case 'checkmate':
      return 'A checkmate is a position where the king is in check and there is no legal move to escape.';
    default:
      return 'A tactical combination of chess moves designed to gain an advantage.';
  }
}

export default {
  TACTICAL_PATTERNS,
  getAvailableTacticalThemes,
  getTacticalThemeDescription
}; 